import { Button, Icon, Notification, Typography } from '@smartgift/frontend-toolkit';
import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AppLoader } from '@hero/atoms';
import {
  AUTOMATION_CONTACTS_TAB,
  AUTOMATION_CREATION_STEPS,
  CONTACT_LIMIT_LIST,
  DEFAULT_CONTACT_LIMIT_LIST,
  QUERY_KEYS
} from '@hero/constants';
import { useAutomationCreationHeader } from '@hero/hooks';
import { PageLayout } from '@hero/layouts';
import { DoubleContactTable, DoubleTagTable } from '@hero/organisms';
import { ROUTE_PATHS } from '@hero/routes';
import { useUpdateAutomation } from '@hero/services';
import { useGetTagList } from '@hero/services/contact-service/hooks';
import { useGetMerchant } from '@hero/services/merchant-service/hooks';
import { useNewAutomationStore, useNotification, useValidateRules } from '@hero/store/hooks';
import { getRecipientsRule } from '@hero/validations';

import * as S from './style';

function NewAutomationRecipients() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isEditContactsModalOpen, setIsEditContactModal] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const header = useAutomationCreationHeader({
    step: 3,
    setIsPageLoading
  });

  const { showNotification } = useNotification();
  const queryClient = useQueryClient();
  const { data: merchantData } = useGetMerchant({ options: { includeConfigs: true } });
  const { code, contacts, name, type, updateContacts } = useNewAutomationStore();
  const { mutateAsync: updateAutomation, isLoading: isLoadingUpdateAutomation } =
    useUpdateAutomation();

  const { data: tagList } = useGetTagList(
    {
      filters: {
        ids: contacts?.tags
      },
      options: {
        includeContacts: true
      }
    },
    { enabled: contacts?.tags?.length > 0 }
  );

  const [defaultAddedRows, setDefaultAddedRows] = useState(
    contacts?.ids?.reduce((result, id) => {
      result[id] = true;
      return result;
    }, {})
  );

  const [defaultAddedTags, setDefaultAddedTags] = useState(
    contacts?.tags?.reduce((result, id) => {
      result[id] = true;
      return result;
    }, {})
  );

  const contactListLimit = useMemo(() => {
    return merchantData?.configs[CONTACT_LIMIT_LIST] ?? DEFAULT_CONTACT_LIMIT_LIST;
  }, [merchantData]);

  const { validate } = useValidateRules();

  const onNextButtonClick = async () => {
    const isValid = await validate([{ rule: getRecipientsRule(), value: contacts }]);
    if (!isValid) return;

    setIsPageLoading(true);
    await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.useGetAllContactIdsKey] });

    try {
      await updateAutomation({
        params: {
          code
        },
        body: {
          contacts,
          meta: {
            creationStep: AUTOMATION_CREATION_STEPS.newAutomationExperience
          }
        }
      });

      navigate(ROUTE_PATHS.NEW_AUTOMATION_EXPERIENCE);
    } finally {
      setIsPageLoading(false);
    }
  };

  useEffect(() => {
    if (!name || !type || !contacts.type) {
      navigate(ROUTE_PATHS.NEW_AUTOMATION_RULES);
    }
  }, [name, type]);

  const onClickBackButton = () => navigate(ROUTE_PATHS.NEW_AUTOMATION_RULES);

  const automationContactOptions = {
    [AUTOMATION_CONTACTS_TAB.tags]: (
      <DoubleTagTable
        selectable
        isDeleteActionShown={false}
        selectedRows={defaultAddedTags}
        onChangeSelectedRows={tags => {
          updateContacts({
            tags: Object.keys(tags),
            ids: []
          });
          setDefaultAddedTags(tags);
        }}
        defaultSelectedTags={selectedTag}
        disabledColumns={[type === 'birthday' ? 'anniversary' : 'birthday']}
        isEditContactsModalOpen={isEditContactsModalOpen}
        setIsEditContactModal={setIsEditContactModal}
      />
    ),
    [AUTOMATION_CONTACTS_TAB.contacts]: (
      <DoubleContactTable
        featureType="automation"
        disabledColumns={[type === 'birthday' ? 'anniversary' : 'birthday']}
        selectedRows={defaultAddedRows}
        onChangeSelectedRows={selectedRows => {
          if (Object.keys(selectedRows ?? {})?.length >= contactListLimit) {
            showNotification({
              message: t('recipientLimitMessage', {
                recipientLimit: contactListLimit
              }),
              title: t('recipientLimitTitle'),
              notificationImage: (
                <Icon name="error" size="xxl" color="var(--color-error-default)" />
              ),
              primaryButtonProps: {
                style: {
                  margin: '0 auto'
                }
              }
            });
            return;
          }

          updateContacts({
            tags: [],
            ids: Object.keys(selectedRows)
          });

          setDefaultAddedRows(selectedRows);
        }}
      />
    )
  };

  return (
    <PageLayout
      header={header}
      navigation={{
        rightButton: {
          children: t('next'),
          onClick: onNextButtonClick,
          loading: isLoadingUpdateAutomation,
          icon: true
        },
        leftButton: {
          children: t('back'),
          onClick: onClickBackButton
        }
      }}
    >
      <S.Wrapper>
        {contacts?.tags?.length > 0 && (
          <S.TagNotificationWrapper>
            {tagList?.docs
              ?.filter(tag => tag.contactsCount === 0)
              .map(tag => (
                <Notification
                  key={tag.id}
                  status="info"
                  isClosable
                  message={
                    <>
                      <Trans
                        i18nKey="contactlessTagNotificationWarning"
                        components={{ b: <b /> }}
                        values={{ tagName: tag.name }}
                      />
                      <Button
                        variant="link"
                        color="neutral"
                        onClick={() => {
                          setSelectedTag([tag]);
                          setIsEditContactModal(true);
                        }}
                      >
                        {t('assignContactNow')}
                      </Button>
                    </>
                  }
                />
              ))}
          </S.TagNotificationWrapper>
        )}
        <Typography variant="subtitle4Bold" style={{ textAlign: 'center' }}>
          {t(
            contacts.type === AUTOMATION_CONTACTS_TAB.contacts
              ? 'whoWouldYouLikeToShare'
              : 'whichTagsWouldYouLikeToInclude'
          )}
        </Typography>
        {automationContactOptions[contacts.type]}
        {isPageLoading && <AppLoader />}
      </S.Wrapper>
    </PageLayout>
  );
}

export { NewAutomationRecipients };
